import { Injectable } from '@angular/core';
import { ProductCategory } from '@features/catalog/models/product-category.models';
import { CategoryDuplicateCreateStep } from '@features/catalog/utils/category-duplicate.util';
import { Observable } from 'rxjs';
import { CatalogCategoryService } from './catalog-category.service';

export interface CategoryDuplicateResult {
  created: ProductCategory[];
  errors: string[];
  failedStep?: CategoryDuplicateCreateStep;
}

@Injectable()
export class CategoryDuplicateOrchestrationService {
  constructor(private readonly categoryService: CatalogCategoryService) {}

  duplicateSubtree(plan: readonly CategoryDuplicateCreateStep[]): Observable<CategoryDuplicateResult> {
    return new Observable((subscriber) => {
      if (!plan.length) {
        subscriber.next({ created: [], errors: [] });
        subscriber.complete();
        return;
      }

      const createdBySourceId = new Map<string, ProductCategory>();
      const created: ProductCategory[] = [];

      const runStep = (index: number): void => {
        if (index >= plan.length) {
          subscriber.next({ created, errors: [] });
          subscriber.complete();
          return;
        }

        const step = plan[index];
        const parentId =
          step.parentSourceId == null
            ? step.targetParentId
            : (createdBySourceId.get(step.parentSourceId)?.id ?? null);

        if (step.parentSourceId != null && parentId == null) {
          subscriber.next({
            created,
            errors: ['No se pudo resolver el padre de la subcategoría duplicada.'],
            failedStep: step,
          });
          subscriber.complete();
          return;
        }

        this.categoryService
          .create({
            name: step.name,
            position: step.position,
            parentId,
            active: true,
            iconName: step.iconName,
            fulfillmentLeadTimeMode: step.fulfillmentLeadTimeMode,
            fulfillmentLeadTimeDays: step.fulfillmentLeadTimeDays,
          })
          .subscribe({
            next: (result) => {
              if (result.errors.length || !result.category) {
                subscriber.next({
                  created,
                  errors: result.errors.length
                    ? result.errors
                    : ['No se pudo crear la categoría duplicada.'],
                  failedStep: step,
                });
                subscriber.complete();
                return;
              }

              createdBySourceId.set(step.sourceId, result.category);
              created.push(result.category);
              runStep(index + 1);
            },
            error: (err: Error) => {
              subscriber.next({
                created,
                errors: [err.message || 'Error de red al duplicar categorías.'],
                failedStep: step,
              });
              subscriber.complete();
            },
          });
      };

      runStep(0);
    });
  }
}
